import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ClipboardList, Pencil, Archive, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { StatusBadge, OverdueBadge } from "@/components/common";
import { SEV_CLS } from "@/components/CriticitaFormDialog";
import { STATE_LABEL } from "@/components/map/MapCanvas";
import { fmtDT } from "@/lib/format";

const Block = ({ title, children, count }) => (
  <div className="mt-4"><p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}{count != null && ` (${count})`}</p>{children}</div>
);

export const AreaPanel = ({ areaId, version, onReport, onCreateOrder, onEdit, onArchived }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/aree/${areaId}`).then((r) => setD(r.data));
  }, [areaId, version]);
  if (!d) return <p className="p-4 text-sm text-muted-foreground">Caricamento…</p>;
  const openCr = d.criticita.filter((c) => !["chiusa", "annullata"].includes(c.status));
  const archive = async () => {
    const reason = window.prompt("Motivazione dell'archiviazione dell'area:");
    if (!reason) return;
    try {
      await api.post(`/aree/${areaId}/archive`, { reason });
      toast.success("Area archiviata");
      onArchived();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const viewPhoto = async (p) => {
    const r = await api.get(`/files/${p.id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  return (
    <div data-testid="area-panel">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d.area.type}</p>
      <h2 className="text-2xl font-extrabold" data-testid="area-panel-title">{d.area.label}</h2>
      <p className="mt-1 text-sm font-semibold" data-testid="area-panel-state">{STATE_LABEL[d.state]}</p>
      <div className="mt-4 grid gap-2">
        <Button onClick={onReport} className="h-12 rounded-xl text-base font-bold" data-testid="area-report-button"><AlertTriangle className="mr-2 h-5 w-5" />Segnala un problema</Button>
        {d.can_edit && <Button variant="outline" onClick={() => onCreateOrder(null)} className="h-11 rounded-xl font-semibold" data-testid="area-create-order-button"><ClipboardList className="mr-2 h-4 w-4" />Crea ordine su quest'area</Button>}
        {d.can_edit && (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onEdit} className="flex-1 rounded-xl" data-testid="area-edit-geometry-button"><Pencil className="mr-1 h-4 w-4" />Modifica geometria</Button>
            <Button variant="ghost" onClick={archive} className="rounded-xl text-red-600" data-testid="area-archive-button"><Archive className="mr-1 h-4 w-4" />Archivia</Button>
          </div>
        )}
      </div>
      <Block title="Criticità aperte" count={openCr.length}>
        {openCr.length === 0 && <p className="text-sm text-muted-foreground">Nessuna criticità aperta.</p>}
        {openCr.map((c) => (
          <Link key={c.id} to={`/criticita/${c.id}`} className="mb-2 block rounded-xl border-l-4 border-red-500 bg-red-50/60 p-3" data-testid={`area-cr-${c.code}`}>
            <div className="flex items-center gap-2"><span className="font-mono text-xs">{c.code}</span><span className={`rounded-full px-2 text-xs font-bold ${SEV_CLS[c.severity]}`}>{c.severity}</span><span className="text-xs">{c.status_label}</span></div>
            <p className="mt-1 text-sm font-semibold">{c.title}</p>
          </Link>
        ))}
      </Block>
      <Block title="Ordini aperti" count={d.open_orders.length}>
        {d.open_orders.map((o) => (
          <Link key={o.id} to={`/ordini/${o.id}`} className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border p-3 text-sm" data-testid={`area-order-${o.code}`}>
            <span className="font-mono text-xs">{o.code}</span><span className="flex-1 font-semibold">{o.title}</span><StatusBadge status={o.status} /><OverdueBadge order={o} />
          </Link>
        ))}
      </Block>
      <Block title="Interventi eseguiti" count={d.done_orders.length}>
        {d.done_orders.slice(0, 8).map((o) => <Link key={o.id} to={`/ordini/${o.id}`} className="block border-b py-2 text-sm last:border-0">{o.code} · {o.title} · {fmtDT(o.closed_at)}</Link>)}
      </Block>
      <Block title="Ultime ispezioni" count={d.inspections.length}>
        {d.inspections.slice(0, 8).map((i) => <p key={i.id} className="border-b py-2 text-sm last:border-0">{fmtDT(i.at)} · {i.category} · <b className={i.esito === "ok" ? "text-emerald-700" : "text-red-600"}>{i.esito}</b> {i.note}</p>)}
      </Block>
      <Block title="Fotografie" count={d.photos.length}>
        <div className="flex flex-wrap gap-2">{d.photos.map((p) => <button key={p.id} onClick={() => viewPhoto(p)} className="inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs"><Camera className="h-3 w-3" />{p.criticita_code}</button>)}</div>
      </Block>
      <Block title="Storico criticità chiuse">
        {d.criticita.filter((c) => c.status === "chiusa").map((c) => <Link key={c.id} to={`/criticita/${c.id}`} className="block border-b py-2 text-sm last:border-0">{c.code} · {c.title} · chiusa {fmtDT(c.closed_at)}</Link>)}
      </Block>
    </div>
  );
};
